import api, {loginApi} from 'sources/api';
import {MediaflySolution, User} from 'records/User';

export interface PasswordLoginRequest {
  captchaResponse?: string;
  email: string;
  password: string;
  remember: boolean;
}

interface TokenLoginRequest {
  token: string;
  userId: string;
}

export interface TokenLoginParams extends TokenLoginRequest {
  solutionList: MediaflySolution[];
}

export interface LogoutParams {
  nextPathname?: string;
  message?: string;
}

export interface FindLoginTypeRequest {
  email: string;
}

export enum LoginType {
  PASSWORD = 'PASSWORD',
  GUM = 'GUM',
  SAML = 'SAML',
}

export interface LoginTypeResponse {
  loginMethod: LoginType;
  samlName: string | null;
  tenantId: string | null;
}

export class AuthSource {
  static async passwordLogin({captchaResponse, email, password, remember}: PasswordLoginRequest) {
    if (captchaResponse) {
      await loginApi.post('/login/captcha', {
        email: email,
        captchaResponse: captchaResponse,
      });
    }

    return loginApi
      .postJson('/login/password', {
        email: email,
        password: password,
        remember: remember,
        referrer: window.location?.ancestorOrigins?.[0] ?? window.document.referrer,
      })
      .then(({user}) => new User(user));
  }

  static tokenLogin({token, userId, solutionList}: TokenLoginParams) {
    return loginApi
      .postJson('/login/token', {
        userId,
        token,
        referrer: window.location?.ancestorOrigins?.[0] ?? window.document.referrer,
      })
      .then(({user}) => {
        user.solutionList = solutionList;
        return new User(user);
      });
  }

  /**
   * This calls the BE to remove the session key from the DB, that way someone can't use the same cookie to log in
   * again if they managed to get their hands on it.
   */
  static logout() {
    return loginApi.post('/login/logout');
  }

  static checkSession() {
    return loginApi.get('/login/has_session').then(res => res.data as boolean);
  }

  static getUserMeInfo() {
    return api.get('/users/me');
  }

  static findLoginType({email}: FindLoginTypeRequest) {
    return loginApi.get(`/login/login_method?email=${email}`).then(response => response.data);
  }
}
